import { useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useParams } from "react-router-dom";
import { FiEye, FiEyeOff } from "react-icons/fi";
import "../css/AddUser.css";

const AddUserForm = ({ defaultRole = "" }) => {
  const navigate = useNavigate();
  const params = useParams(); // optional if you pass role via route
  const [loading, setLoading] = useState(false);

  const [user, setUser] = useState({
    firstName: "",
    lastName: "",
    emailId: "",
    password: "",
    phoneNo: "",
    street: "",
    city: "",
    pincode: "",
    role: "",
  });

  // derive role defensively: prop -> route param -> url fallback
  if (!user.role) {
    if (defaultRole) user.role = defaultRole;
    else if (params.role)
      user.role = params.role; // if route like /register/:role
    else if (document.URL.indexOf("admin") !== -1) user.role = "Admin";
    else if (document.URL.indexOf("delivery") !== -1) user.role = "Delivery";
    else if (document.URL.indexOf("customer") !== -1) user.role = "Customer";
    else if (document.URL.indexOf("supplier") !== -1) user.role = "Supplier";
  }

  const [showPassword, setShowPassword] = useState(false);

  const handleUserInput = (e) => {
    const { name, value } = e.target;
    setUser((prev) => ({ ...prev, [name]: value }));
  };

  // small client-side validation
  const validate = () => {
    if (!user.firstName.trim()) return "Enter First Name";
    if (!user.emailId.trim()) return "Enter Email Id";
    if (!/^\S+@\S+\.\S+$/.test(user.emailId)) return "Enter a valid email";
    if (!user.password || user.password.length < 6)
      return "Password must be at least 6 characters";
    if (!/^\d{10}$/.test(String(user.phoneNo)))
      return "Enter valid 10-digit phone";
    if (user.pincode && !/^\d{5,6}$/.test(String(user.pincode)))
      return "Enter valid pincode (5-6 digits)";
    if (!user.role) return "Role is not selected";
    return null;
  };

  const saveUser = async (event) => {
    event.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err, { position: "top-center", autoClose: 1500 });
      return;
    }

    setLoading(true);
    try {
      const resp = await fetch("https://goldenhides.in/api/user/register", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(user),
      });

      const body = await resp.json();
      if (resp.ok && body.success) {
        toast.success(body.responseMessage || "Registered successfully", {
          position: "top-center",
          autoClose: 2000,
        });

        // clear form or redirect
        setTimeout(() => {
          navigate("/user/verify/register", {
            state: body.users ? body.users[0] : {},
          });
        }, 1200);
      } else {
        toast.error(body.responseMessage || "Registration failed", {
          position: "top-center",
          autoClose: 2000,
        });
      }
    } catch (e) {
      console.error("Register error", e);
      toast.error("Server error, please try again later", {
        position: "top-center",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="adduser-page auth-background">
      <div className="mt-2 d-flex align-items-center justify-content-center ...">
        <div
          className="card form-card text-color custom-bg adduser-card"
          style={{ maxWidth: "50rem", width: "100%" }}
        >
          <div className="card-header bg-color custom-bg-text text-center">
            <h5 className="card-title">Register {user.role}</h5>
          </div>

          <div className="card-body">
            <form className="row g-3" onSubmit={saveUser} noValidate>
              <div className="col-md-6 mb-3 text-color">
                <label htmlFor="firstName" className="form-label">
                  <b>First Name</b>
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  className="form-control"
                  value={user.firstName}
                  onChange={handleUserInput}
                  autoFocus
                  required
                />
              </div>

              <div className="col-md-6 mb-3 text-color">
                <label htmlFor="lastName" className="form-label">
                  <b>Last Name</b>
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  className="form-control"
                  value={user.lastName}
                  onChange={handleUserInput}
                />
              </div>

              <div className="col-md-6 mb-3 text-color">
                <label htmlFor="emailId" className="form-label">
                  <b>Email Id</b>
                </label>
                <input
                  id="emailId"
                  name="emailId"
                  type="email"
                  className="form-control"
                  value={user.emailId}
                  onChange={handleUserInput}
                  required
                />
              </div>

              <div className="col-md-6 mb-3 text-color position-relative">
                <label htmlFor="password" className="form-label">
                  <b>Password</b>
                </label>
                <div className="input-group">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    className="form-control"
                    value={user.password}
                    onChange={handleUserInput}
                    required
                    minLength={6}
                  />
                  <span
                    className="input-group-text"
                    style={{
                      cursor: "pointer",
                      backgroundColor: "white",
                      borderLeft: "none",
                    }}
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? (
                      <FiEyeOff className="text-color fs-5" />
                    ) : (
                      <FiEye className="text-color fs-5" />
                    )}
                  </span>
                </div>
                <div className="form-text">Use at least 6 characters.</div>
              </div>

              <div className="col-md-6 mb-3">
                <label htmlFor="phoneNo" className="form-label">
                  <b>Mobile No</b>
                </label>
                <input
                  id="phoneNo"
                  name="phoneNo"
                  type="tel"
                  inputMode="numeric"
                  pattern="\d{10}"
                  className="form-control"
                  value={user.phoneNo}
                  onChange={handleUserInput}
                  required
                />
                <div className="form-text">10-digit mobile number</div>
              </div>

              <div className="col-md-6 mb-3">
                <label htmlFor="street" className="form-label">
                  <b>Street</b>
                </label>
                <textarea
                  id="street"
                  name="street"
                  className="form-control"
                  rows="2"
                  value={user.street}
                  onChange={handleUserInput}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label htmlFor="city" className="form-label">
                  <b>City</b>
                </label>
                <input
                  id="city"
                  name="city"
                  type="text"
                  className="form-control"
                  value={user.city}
                  onChange={handleUserInput}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label htmlFor="pincode" className="form-label">
                  <b>Pincode</b>
                </label>
                <input
                  id="pincode"
                  name="pincode"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{5,6}"
                  className="form-control"
                  value={user.pincode}
                  onChange={handleUserInput}
                />
              </div>

              <div className="d-flex align-items-center justify-content-center">
                <button
                  type="submit"
                  className="btn bg-color custom-bg-text col-md-3"
                  disabled={loading}
                >
                  {loading ? "Registering..." : "Register User"}
                </button>
              </div>

              <ToastContainer />
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUserForm;
